import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BalanceGraph, AmortTable, buildAmortization } from './_finHelpers';

// Home Equity Loan Calculator
export default function HomeEquityLoanCalc() {
  const [amount, setAmount] = useState(150000);
  const [rate, setRate] = useState(8);
  const [term, setTerm] = useState(15);
  const [includeClosing, setIncludeClosing] = useState(false);
  const [closing, setClosing] = useState(2000);
  const c = useMemo(() => {
    const principal = amount + (includeClosing ? closing : 0);
    return buildAmortization(principal, rate, term*12);
  }, [amount, rate, term, includeClosing, closing]);
  return (
    <>
      <CalcShell look="cnet" title="Home Equity Loan Calculator" subtitle="Calculate monthly payments and total interest for a fixed-rate home equity loan (second mortgage)."
        about={<>
          <p>A <b>home equity loan</b> is a fixed-rate second mortgage against the equity in your home. Unlike a HELOC, it disburses a lump sum and requires equal amortizing payments from day one.</p>
        </>}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="field"><label>Loan amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={amount} onChange={e=>setAmount(+e.target.value||0)} className="flex-1"/></div></div>
            <div className="field"><label>Interest rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
            <div className="field"><label>Loan term</label><div className="flex gap-2"><input type="number" value={term} onChange={e=>setTerm(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">years</span></div></div>
            <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={includeClosing} onChange={e=>setIncludeClosing(e.target.checked)}/> Include closing costs</label>
            {includeClosing && <div className="field mt-2"><label>Closing costs ($)</label><input type="number" value={closing} onChange={e=>setClosing(+e.target.value||0)}/></div>}
          </div>
          <div>
            <div className="result-panel mb-4"><div className="result-title">Monthly pay</div><div className="result-value" data-testid="hel-monthly">{fmt(c.M)}</div></div>
            <table className="data-table"><tbody>
              <tr><td>Total of {term*12} loan payments</td><td className="text-right font-semibold">{fmt(c.totalPaid)}</td></tr>
              <tr><td>Total interest</td><td className="text-right text-orange-600">{fmt(c.totalInterest)}</td></tr>
            </tbody></table>
            <div className="mt-4"><PieChart parts={[{label:'Loan amount', value:amount, color:'#2563eb'},{label:'Interest', value:c.totalInterest, color:'#10b981'}]} size={170}/></div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div><h3 className="font-bold text-slate-900 mb-3">Amortization Schedule</h3><AmortTable schedule={c}/></div>
          <div><h3 className="font-bold text-slate-900 mb-3">Balance / Interest / Payment</h3><BalanceGraph yearly={c.yearly} startBalance={amount}/></div>
        </div>
      </CalcShell>
    </>
  );
}
